import { Injectable } from '@angular/core';
import { List } from 'immutable';
import { HttpResponse } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { BaseDataStore } from './base-data.store';
import { AuditHistoryApiService } from '../api/audit-history.api.service';
import { PaginationHelper } from '../helpers/pagination.helper';
import { IAuditHistoryEntry, IAuditHistoryFilterOptions, IAuditHistoryQuery } from '../../models/history/audit-history.model';

/**
 * State for the edit panel's History tab.
 *
 * Deliberately passive: constructing it issues no request, and nothing here reacts to a node being
 * selected. The tab is on-demand by requirement, so the component decides when to load and this
 * store only does what it is told. Adding an automatic fetch here would quietly reintroduce a query
 * on every node selection.
 */
@Injectable({
  providedIn: 'root',
})
export class AuditHistoryStore extends BaseDataStore {
  /** Offered on the rows-per-page control - see setPageSize. */
  public static readonly PageSizeOptions = [10, 25, 50];

  private _entries: BehaviorSubject<List<IAuditHistoryEntry>> = new BehaviorSubject(List<IAuditHistoryEntry>([]));
  private _isLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _lastError: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private _filterOptions: BehaviorSubject<IAuditHistoryFilterOptions> = new BehaviorSubject<IAuditHistoryFilterOptions>({ actors: [], labels: [], actions: [] });
  /** Whether the last attempt to load the dropdown options failed - see loadFilterOptions. */
  private _filterOptionsFailed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** The page currently held - see goToPage. 0 while nothing has been loaded. */
  private _pageNumber: BehaviorSubject<number> = new BehaviorSubject(0);
  /** How many entries the current filter matches in total, for "Page X of Y" - see fetchTotalCount. */
  private _totalCount: BehaviorSubject<number> = new BehaviorSubject(0);
  /**
   * How many entries a page holds - see setPageSize. Seeded from PaginationHelper, the same source
   * every other paged admin screen uses for its default, rather than a number invented for this tab.
   */
  private _pageSize: BehaviorSubject<number> = new BehaviorSubject(this.paginationHelper.getDefaultPageSize());

  public $entries: Observable<List<IAuditHistoryEntry>> = this._entries.asObservable();
  public $isLoading: Observable<boolean> = this._isLoading.asObservable();
  public $lastError: Observable<string | null> = this._lastError.asObservable();
  public $filterOptions: Observable<IAuditHistoryFilterOptions> = this._filterOptions.asObservable();
  public $filterOptionsFailed: Observable<boolean> = this._filterOptionsFailed.asObservable();
  public $pageNumber: Observable<number> = this._pageNumber.asObservable();
  public $totalCount: Observable<number> = this._totalCount.asObservable();
  public $pageSize: Observable<number> = this._pageSize.asObservable();

  private _currentNodeId: string | null = null;
  private _currentProjectId: string | null = null;
  private _currentQuery: IAuditHistoryQuery | null = null;
  private _filterOptionsToken = 0;
  /** Separate from _requestToken: a stale count must not overwrite a newer one, but must not cancel
   *  an in-flight page load either - they are two independent requests per filter change. */
  private _countToken = 0;

  /**
   * Incremented on every load. A response whose token no longer matches is discarded: selecting a
   * second node while the first is still in flight would otherwise paint the wrong item's history.
   */
  private _requestToken = 0;

  constructor(
    private readonly auditHistoryApi: AuditHistoryApiService,
    private readonly paginationHelper: PaginationHelper,
  ) {
    super();
    this.behaviorSubjectList.push(this._entries, this._isLoading, this._lastError, this._pageNumber, this._totalCount, this._pageSize);
  }

  public get currentNodeId(): string | null {
    return this._currentNodeId;
  }

  /**
   * Loads the first page for a node, replacing whatever was held. Also the way to re-apply a
   * changed filter - paging restarts, because page 3 of one filter is meaningless under another.
   *
   * Also refreshes the total count under the same filter, which is what lets the pane show an exact
   * "Page X of Y" rather than only ever knowing whether one more page exists.
   */
  public loadFirstPage(nodeId: string, projectId: string, query?: IAuditHistoryQuery | null): void {
    if (!nodeId || !projectId) {
      this.clear();
      return;
    }

    this._currentNodeId = nodeId;
    this._currentProjectId = projectId;
    this._currentQuery = query ?? null;

    const previousPage = this._pageNumber.getValue();
    this._pageNumber.next(1);

    this._entries.next(List<IAuditHistoryEntry>([]));
    this._lastError.next(null);
    this._isLoading.next(true);

    this.fetchPage(1, previousPage || 1);
    this.fetchTotalCount();
  }

  /**
   * Jumps to a specific page of the current filter, replacing whatever was held - the pane shows one
   * page at a time rather than an ever-growing list, so there is nothing to append to.
   *
   * A no-op while a load is already in flight, before anything has been loaded, or for the page
   * already on screen - the last of those matters because Prev/Next call this with currentPage ± 1,
   * and a stray double-click must not re-request the page that is already showing.
   */
  public goToPage(pageNumber: number): void {
    if (!this._currentNodeId || !this._currentProjectId) {
      return;
    }
    if (!pageNumber || pageNumber < 1 || this._isLoading.getValue()) {
      return;
    }

    const previousPage = this._pageNumber.getValue();
    if (pageNumber === previousPage) {
      return;
    }

    this._pageNumber.next(pageNumber);
    this._isLoading.next(true);

    this.fetchPage(pageNumber, previousPage);
  }

  /**
   * Changes how many entries a page holds and restarts at page 1 under it - page 3 of 10 rows is not
   * page 3 of 50, so keeping the page number across a size change would show the wrong slice.
   *
   * Persists across filter and item changes rather than resetting: it is a reading preference, the
   * same way the admin log screen keeps whatever rows-per-page the user last picked.
   */
  public setPageSize(pageSize: number): void {
    if (!this._currentNodeId || !this._currentProjectId) {
      return;
    }
    if (!pageSize || pageSize < 1 || pageSize === this._pageSize.getValue()) {
      return;
    }

    this._pageSize.next(pageSize);

    const previousPage = this._pageNumber.getValue();
    this._pageNumber.next(1);
    this._isLoading.next(true);

    this.fetchPage(1, previousPage || 1);
  }

  /**
   * Loads the dropdown options for one item. Separate from loadFirstPage because it does not change
   * when a filter does - the options describe the item's whole history, not the filtered view, so
   * refetching them on every filter change would both cost a request per keystroke and shrink the
   * list to whatever the current filter left, making it impossible to widen the filter again.
   *
   * A real failure is flagged via `$filterOptionsFailed` rather than swallowed: the API service now
   * rethrows, and rendering that the same way as a brand-new item's empty dropdowns would hide the
   * failure from the reader with no way to retry it.
   */
  public loadFilterOptions(nodeId: string, projectId: string): void {
    if (!nodeId || !projectId) {
      this._filterOptions.next({ actors: [], labels: [], actions: [] });
      this._filterOptionsFailed.next(false);
      return;
    }

    // Its own counter rather than the page loads' _requestToken: incrementing that one here would
    // discard a page request already in flight. Same staleness guard, separate lifetime - selecting
    // another item while this is outstanding must not leave the previous item's people in the list.
    const token = ++this._filterOptionsToken;
    this._filterOptionsFailed.next(false);

    this.subscribers.filterOptions = this.auditHistoryApi.getFilterOptions(nodeId, projectId).subscribe({
      next: (options) => {
        if (token !== this._filterOptionsToken) {
          return;
        }
        this._filterOptions.next(options);
      },
      error: () => {
        if (token !== this._filterOptionsToken) {
          return;
        }
        this._filterOptionsFailed.next(true);
      },
    });
  }

  /**
   * The full history for the item currently held, as a CSV file, under the filter on screen.
   *
   * A passthrough rather than store state: the file is handed straight to the browser, so there is
   * nothing for the store to hold and nothing for the pane to re-render. `isTestNode` and
   * `currentLabelId` are not tracked as store state the way node/project/query are - neither ever
   * changes which rows come back, only which columns the file has - so the caller (which already has
   * both as inputs) passes them through here.
   */
  public exportCurrent(isTestNode = false, currentLabelId: string | null = null): Observable<HttpResponse<string>> | null {
    if (!this._currentNodeId || !this._currentProjectId) {
      return null;
    }

    return this.auditHistoryApi.exportNodeHistory(this._currentNodeId, this._currentProjectId, this._currentQuery, isTestNode, currentLabelId);
  }

  public clear(): void {
    this._currentNodeId = null;
    this._currentProjectId = null;
    this._currentQuery = null;
    this._requestToken += 1;
    this._countToken += 1;

    this._pageNumber.next(0);
    this._totalCount.next(0);
    this._entries.next(List<IAuditHistoryEntry>([]));
    this._isLoading.next(false);
    this._lastError.next(null);

    // The dropdowns describe the item, so they go when the item does - otherwise reopening the tab
    // on a different item offers the previous one's people until the new options arrive.
    this._filterOptionsToken += 1;
    this._filterOptions.next({ actors: [], labels: [], actions: [] });
    this._filterOptionsFailed.next(false);
  }

  /**
   * Fetches one page and replaces whatever was held with it - the pane shows exactly one server page
   * at a time, so there is never anything to merge or de-duplicate against.
   *
   * `previousPage` is what $pageNumber reverts to on failure: the page that failed to load was never
   * shown, so Prev/Next must keep reporting the page actually on screen rather than one that errored.
   */
  private fetchPage(pageNumber: number, previousPage: number): void {
    const token = ++this._requestToken;
    const nodeId = this._currentNodeId as string;
    const projectId = this._currentProjectId as string;

    if (this.subscribers.loadHistory) {
      this.subscribers.loadHistory.unsubscribe();
    }

    this.subscribers.loadHistory = this.auditHistoryApi
      .getNodeHistory(nodeId, projectId, pageNumber, this._pageSize.getValue(), this._currentQuery)
      .pipe(
        catchError((err) => {
          if (token === this._requestToken) {
            this._lastError.next(this.formatError(err));
            this._isLoading.next(false);
            this._pageNumber.next(previousPage);
          }
          return EMPTY;
        }),
        tap(() => {
          if (token === this._requestToken) {
            this._isLoading.next(false);
            this._lastError.next(null);
          }
        }),
      )
      .subscribe((entries: IAuditHistoryEntry[]) => {
        // Stale response - a different node (or a newer request for this one) has since been asked
        // for. Dropping it is the whole reason the token exists.
        if (token !== this._requestToken) {
          return;
        }

        this._entries.next(List<IAuditHistoryEntry>(entries ?? []));
      });
  }

  /**
   * How many entries the current filter matches in total, independent of the page load above - the
   * pane needs both, and fetching them together would make a stale filter's count race a newer one's
   * list, or vice versa. Its own token rather than _requestToken for the same reason
   * loadFilterOptions has one: incrementing the page-load token here would discard a page request
   * already in flight.
   */
  private fetchTotalCount(): void {
    const token = ++this._countToken;
    const nodeId = this._currentNodeId as string;
    const projectId = this._currentProjectId as string;

    if (this.subscribers.historyCount) {
      this.subscribers.historyCount.unsubscribe();
    }

    this.subscribers.historyCount = this.auditHistoryApi
      .getNodeHistoryCount(nodeId, projectId, this._currentQuery)
      // getNodeHistoryCount rethrows on failure like every other read here. Uncaught, that would
      // surface as an unhandled Observable error - a global toast next to a page that loaded fine -
      // and leave _totalCount on whatever it last held. Dropping it here keeps that stale figure
      // rather than crashing the subscription; the page load's own success/failure is unaffected.
      .pipe(catchError(() => EMPTY))
      .subscribe((count) => {
        if (token !== this._countToken) {
          return;
        }
        this._totalCount.next(count ?? 0);
      });
  }

  private formatError(err: unknown): string {
    if (!err) return 'Unknown error';
    if (typeof err === 'string') return err;
    return (err as { message?: string; statusText?: string }).message ?? (err as { statusText?: string }).statusText ?? 'Request failed';
  }
}
